import React, { useEffect, useState } from 'react';
import { Settings } from 'luxon';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from './components/common/ErrorBoundary';
import PortalRoutes from './components/common/PortalRoutes';
import { getUser, logout } from './services/authService';
import UnauthenticatedRoutes from './components/common/UnauthenticatedRoutes';
import LoadingComponent from './components/common/LoadingComponent';
import { SuccessLoginData } from './types/general.types';
import {
  CARRIER_ACCESS_TOKEN_KEY,
  CARRIER_COUNTRY_CODE_STOARGE_KEY,
  CARRIER_LOCALE_STOARGE_KEY,
  CARRIER_TIMEZONE_STOARGE_KEY,
  DEFAULT_LOCALE,
  DEFAULT_TIMEZONE,
  UNAUTH_CARRIER_LOCALE_STOARGE_KEY,
} from './constants';
import {
  delay,
  getAuthUserData,
  getLocale,
  getTimezone,
  setLocalStorageKey,
  setSessionStorageKey,
} from './utils';

function App() {
  const [user, setUser] = useState<{} | null>(getUser());
  const [isLoading, setIsLoading] = useState(false);

  const { i18n } = useTranslation();

  const setLoggedUserData = async () => {
    const loggedUser = getUser();
    const locale = getLocale();
    Settings.defaultLocale = locale;
    Settings.defaultZone = getTimezone();

    if (locale) {
      i18n.changeLanguage(locale);
    }
    if (loggedUser) {
      setUser(loggedUser);
      const timeout = loggedUser.expires_at * 1000 - Date.now();
      if (timeout > 0) {
        await delay(timeout);
        setIsLoading(true);
        await delay(1000);
        logout();
      }
    }
  };

  useEffect(() => {
    setLoggedUserData();
  }, []);

  const setLoginData = (data: SuccessLoginData) => {
    const { accessToken, userSettings } = data;
    const { locale, timezone, countryCode } = userSettings;
    if (locale) {
      setSessionStorageKey(CARRIER_LOCALE_STOARGE_KEY, locale);
      setLocalStorageKey(UNAUTH_CARRIER_LOCALE_STOARGE_KEY, locale);
    } else {
      setSessionStorageKey(CARRIER_LOCALE_STOARGE_KEY, DEFAULT_LOCALE);
    }
    if (timezone) {
      setSessionStorageKey(CARRIER_TIMEZONE_STOARGE_KEY, timezone);
    } else {
      setSessionStorageKey(CARRIER_TIMEZONE_STOARGE_KEY, DEFAULT_TIMEZONE);
    }
    if (countryCode) {
      setSessionStorageKey(CARRIER_COUNTRY_CODE_STOARGE_KEY, countryCode);
    }
    setSessionStorageKey(
      CARRIER_ACCESS_TOKEN_KEY,
      JSON.stringify(getAuthUserData(accessToken))
    );
    setLoggedUserData();
  };

  return (
    <div className='App'>
      <ErrorBoundary>
        {isLoading && <LoadingComponent />}
        {user ? (
          <PortalRoutes />
        ) : (
          <UnauthenticatedRoutes setLoginData={setLoginData} />
        )}
      </ErrorBoundary>
    </div>
  );
}

export default App;
